import { TTimelineItem } from "../types";
import { useTimelineAnimations } from "./useTimelineAnimations";
import { useTimelineRows } from "./useTimelineRows";
import { useTimelineViewport } from "./useTimelineViewport";
import { useTimelineWheel } from "./useTimelineWheel";

type UseTimelineStateProps = {
  items: TTimelineItem[];
  durationSeconds: number;
  timelineRefs: React.RefObject<HTMLDivElement | null>[];
};

export const useTimelineState = ({ items, durationSeconds, timelineRefs }: UseTimelineStateProps) => {
  // Row calculation and collapse state.
  const { renderableRows, totalHeight, toggleCollapsed } = useTimelineRows({ items });

  // Viewport and scroll state. (Scroll position is preserved across collapse
  // toggles automatically; this hook clamps `scrollTop` if the new content is
  // shorter than the viewport.)
  const {
    pixelsPerSecond,
    setPixelsPerSecond,
    scrollLeft,
    setScrollLeft,
    scrollTop,
    setScrollTop,
    viewportWidth,
    maxScroll,
    maxScrollTop,
    setViewport,
    zoom,
  } = useTimelineViewport({ durationSeconds, totalHeight });

  // Wheel event handling.
  useTimelineWheel({
    timelineRefs,
    durationSeconds,
    pixelsPerSecond,
    setPixelsPerSecond,
    scrollLeft,
    setScrollLeft,
    setScrollTop,
    viewportWidth,
    maxScroll,
    maxScrollTop,
  });

  // Animations.
  const { animatedTotalHeight, transitions } = useTimelineAnimations({
    renderableRows,
    totalHeight,
  });

  return {
    pixelsPerSecond,
    scrollLeft,
    scrollTop,
    transitions,
    animatedTotalHeight,
    maxScroll,
    renderableRows,
    handleCollapseToggle: toggleCollapsed,
    setViewport,
    setScrollTop,
    setPixelsPerSecond,
    setScrollLeft,
    zoom,
  };
};
